import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest, buildApiUrl } from '../../../api/client';
import '../quotes/quote-generator.css';

type DeficiencyRow = {
    _id: string;
    description: string;
    severity: 'critical' | 'major' | 'minor';
    status: 'open' | 'in_progress' | 'resolved';
    photos: string[];
    notes?: string;
    checklistItemId: string;
    clientId?: { firstName?: string; lastName?: string; buildingName?: string; address?: string } | null;
    inspectionId?: { siteAddress?: string; createdAt?: string } | null;
    technicianName?: string;
    inspectionDate?: string;
    linkedQuoteId?: unknown;
};

type ClientOption = { _id: string; firstName?: string; lastName?: string; buildingName?: string };

export default function DeficienciesBoard(): React.JSX.Element {
    const navigate = useNavigate();
    const [rows, setRows] = useState<DeficiencyRow[]>([]);
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [filterClientId, setFilterClientId] = useState<string>('');
    const [busyId, setBusyId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const load = useCallback(async () => {
        setErrorMessage(null);
        const qs = new URLSearchParams();
        if (filterStatus) qs.set('status', filterStatus);
        if (filterClientId) qs.set('clientId', filterClientId);
        const q = qs.toString();
        const res = await apiRequest<{ success: boolean; data: DeficiencyRow[] }>(
            `/api/deficiencies${q ? `?${q}` : ''}`
        );
        setRows(res.data || []);
    }, [filterStatus, filterClientId]);

    useEffect(() => {
        apiRequest<{ data: ClientOption[] }>('/api/clients?limit=500')
            .then((r) => setClients(Array.isArray(r.data) ? r.data : []))
            .catch(() => setClients([]));
    }, []);

    useEffect(() => {
        load().catch((e) => setErrorMessage(e instanceof Error ? e.message : 'Failed to load'));
    }, [load]);

    const grouped = useMemo(() => {
        const map = new Map<string, { label: string; items: DeficiencyRow[] }>();
        for (const row of rows) {
            const c = row.clientId;
            const label =
                (c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '') ||
                c?.buildingName ||
                c?.address ||
                'Unknown client';
            const key = (c as { _id?: string })?._id || label;
            if (!map.has(key)) map.set(key, { label, items: [] });
            map.get(key)!.items.push(row);
        }
        return Array.from(map.entries()).sort((a, b) => a[1].label.localeCompare(b[1].label));
    }, [rows]);

    async function patchStatus(id: string, status: DeficiencyRow['status']) {
        setBusyId(id);
        setErrorMessage(null);
        try {
            await apiRequest(`/api/deficiencies/${id}`, {
                method: 'PATCH',
                json: { status }
            });
            await load();
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Update failed');
        } finally {
            setBusyId(null);
        }
    }

    async function convertToQuote(id: string) {
        setBusyId(id);
        setErrorMessage(null);
        try {
            const res = await apiRequest<{ success: boolean; data: { quoteId: string } }>(
                `/api/deficiencies/${id}/convert-to-quote`,
                { method: 'POST' }
            );
            navigate(`/admin/quotes/new?id=${encodeURIComponent(res.data.quoteId)}`);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not create quote');
        } finally {
            setBusyId(null);
        }
    }

    function severityStyle(sev: DeficiencyRow['severity']) {
        if (sev === 'critical') return { bg: 'rgba(192,57,43,0.12)', color: '#c0392b' };
        if (sev === 'major') return { bg: 'rgba(230,168,23,0.15)', color: '#b8860b' };
        return { bg: 'rgba(59,130,246,0.12)', color: '#2563eb' };
    }

    const openRows = rows.filter((r) => r.status === 'open' || r.status === 'in_progress');

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '960px' }}>
                    <header style={{ marginBottom: '24px' }}>
                        <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Deficiency board</h1>
                        <p style={{ color: 'var(--dash-muted)', margin: 0 }}>
                            Open items: {openRows.length}. Track remediation from inspection through quote and job.
                        </p>
                    </header>

                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}

                    <div
                        className="quote-card"
                        style={{
                            marginBottom: '20px',
                            padding: '16px 20px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                            alignItems: 'center'
                        }}
                    >
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.78rem', fontWeight: 700 }}>
                            Status
                            <select
                                className="form-input"
                                style={{ minWidth: '160px' }}
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="open">Open</option>
                                <option value="in_progress">In progress</option>
                                <option value="resolved">Resolved</option>
                            </select>
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.78rem', fontWeight: 700 }}>
                            Client
                            <select
                                className="form-input"
                                style={{ minWidth: '200px' }}
                                value={filterClientId}
                                onChange={(e) => setFilterClientId(e.target.value)}
                            >
                                <option value="">All clients</option>
                                {clients.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.buildingName || c._id}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {grouped.length === 0 ? (
                        <p style={{ color: 'var(--dash-muted)' }}>No deficiencies match your filters.</p>
                    ) : (
                        grouped.map(([groupKey, group]) => (
                            <div key={groupKey} className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                                <div className="quote-section-title" style={{ marginBottom: '14px' }}>
                                    {group.label}
                                </div>
                                <div style={{ display: 'grid', gap: '14px' }}>
                                    {group.items.map((row) => (
                                        <div
                                            key={row._id}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '56px 1fr auto',
                                                gap: '12px',
                                                alignItems: 'start',
                                                paddingBottom: '14px',
                                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                                            }}
                                        >
                                            <div>
                                                {row.photos?.[0] ? (
                                                    <a href={buildApiUrl(row.photos[0])} target="_blank" rel="noopener noreferrer">
                                                        <img
                                                            src={buildApiUrl(row.photos[0])}
                                                            alt=""
                                                            style={{
                                                                width: 56,
                                                                height: 56,
                                                                objectFit: 'cover',
                                                                borderRadius: 8
                                                            }}
                                                        />
                                                    </a>
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: 56,
                                                            height: 56,
                                                            borderRadius: 8,
                                                            background: 'rgba(0,0,0,0.06)'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{row.description}</div>
                                                <div style={{ fontSize: '0.82rem', color: 'var(--dash-muted)' }}>
                                                    {row.inspectionId?.siteAddress || '—'}
                                                    {row.technicianName ? ` · ${row.technicianName}` : ''}
                                                    {row.inspectionDate || row.inspectionId?.createdAt
                                                        ? ` · ${new Date((row.inspectionDate || row.inspectionId?.createdAt) as string).toLocaleDateString()}`
                                                        : ''}
                                                </div>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        marginTop: '6px',
                                                        padding: '2px 8px',
                                                        borderRadius: 999,
                                                        fontSize: '0.72rem',
                                                        fontWeight: 800,
                                                        ...severityStyle(row.severity)
                                                    }}
                                                >
                                                    {row.severity}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
                                                <select
                                                    className="form-input"
                                                    style={{ fontSize: '0.85rem' }}
                                                    value={row.status}
                                                    disabled={busyId === row._id}
                                                    onChange={(e) =>
                                                        patchStatus(row._id, e.target.value as DeficiencyRow['status'])
                                                    }
                                                >
                                                    <option value="open">Open</option>
                                                    <option value="in_progress">In progress</option>
                                                    <option value="resolved">Resolved</option>
                                                </select>
                                                {!row.linkedQuoteId && (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                                                        disabled={busyId === row._id}
                                                        onClick={() => convertToQuote(row._id)}
                                                    >
                                                        Create quote
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </AppShell>
    );
}
